import { Authenticated, useTranslate } from "@refinedev/core";
import { Flex, Spin } from "antd";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import { CircleNotchIcon } from "@phosphor-icons/react";
import useInitSubscription from "pages/pricing/hooks/useInitSubscription";
import { useSession } from "hooks/useSession";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { BUSINESS_FEATURES } from "./constants";
import imgBusiness from "../../assets/img-business.png";
import { radius } from "@libertify/ui";

// We render the Stripe pricing table directly here — the same embed used by the
// "Renew Now" PricingModal on /subscription. The personalized plan-card UI is
// kept (commented out) at the bottom of this file: it was built to show prices
// and subscription/checkout links returned by the backend, but that API was
// never delivered. Once the backend returns the plan prices and subscription
// links via the API, restore the card implementation below and remove the
// Stripe pricing table.
const SubscriptionPlansPage = () => {
  const t = useTranslate();
  const { trackEvent } = useAnalytics();
  const { currentOrganization: organization } = useSession();
  const { params, isLoading, init } = useInitSubscription({
    organizationId: organization?.id ?? "",
  });
  const hasInitialized = useRef(false);
  const pricingTableId = import.meta.env.VITE_STRIPE_PRICING_TABLE_ID;
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  // Load Stripe Pricing Table script
  useEffect(() => {
    if (!document.querySelector('script[src*="pricing-table"]')) {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/pricing-table.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Initialize subscription params once the organization is known
  useEffect(() => {
    if (organization?.id && !hasInitialized.current) {
      hasInitialized.current = true;
      init();
      trackEvent(AnalyticsEvents.PricingView, {
        container: "pricing_page",
        pricing_table_id: pricingTableId,
      });
    }
  }, [organization?.id, init, trackEvent, pricingTableId]);

  return (
    <Authenticated key="subscriptionPlans">
      <PageSB>
        <PricingTableWrapSB>
          {isLoading || !params ? (
            <Flex justify="center" align="center" style={{ minHeight: 400 }}>
              <Spin indicator={<CircleNotchIcon style={{ fontSize: 48 }} />} />
            </Flex>
          ) : (
            <stripe-pricing-table
              pricing-table-id={pricingTableId}
              publishable-key={publishableKey}
              customer-session-client-secret={
                params.customer_session_client_secret ?? undefined
              }
              client-reference-id={params.client_reference_id}
              customer-email={params.customer_email}
            />
          )}
        </PricingTableWrapSB>

        <BusinessBannerSB>
          <BusinessImgSB src={imgBusiness} alt="" />
          <BusinessLeftSB>
            <BusinessLabelSB>
              {t("subscriptionPlans.business.label")}
            </BusinessLabelSB>
            <BusinessTitleSB>
              {t("subscriptionPlans.business.title")}
            </BusinessTitleSB>
            <BusinessDescription>
              {t("subscriptionPlans.business.description")}
            </BusinessDescription>
            <BusinessFeaturesRowSB>
              {BUSINESS_FEATURES.map((f) => (
                <BusinessFeatureItemSB key={f}>{t(f)}</BusinessFeatureItemSB>
              ))}
            </BusinessFeaturesRowSB>
          </BusinessLeftSB>
          <BusinessCTABtnSB type="button">
            {t("subscriptionPlans.business.cta")}
          </BusinessCTABtnSB>
        </BusinessBannerSB>
      </PageSB>
    </Authenticated>
  );
};

const PageSB = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 24px 48px;

  @media (width <= 768px) {
    padding: 16px 16px 48px;
  }
`;

const PricingTableWrapSB = styled.div`
  width: 100%;
  margin-bottom: 32px;

  stripe-pricing-table {
    display: block;
    width: 100%;
  }
`;

const BusinessBannerSB = styled.div`
  background: #111827;
  border-radius: ${radius.box}px;
  padding: 40px 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  overflow: hidden;

  @media (width <= 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 32px 24px;
    gap: 28px;
  }
`;

const BusinessImgSB = styled.img`
  width: 20%;
  flex-shrink: 0;
  align-self: stretch;
  object-fit: cover;
  border-radius: ${radius.box}px;
  margin: -40px 0 -40px -48px;

  @media (width <= 768px) {
    display: none;
  }
`;

const BusinessLeftSB = styled.div`
  flex: 1;
  min-width: 0;
`;

const BusinessLabelSB = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #14b8a6;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
  display: block;
`;

const BusinessDescription = styled.h3`
  font-size: 15px;
  font-weight: 300;
  color: #ffffff;
  margin: 0 0 24px;
  line-height: 1.4;
  max-width: 520px;
`;

const BusinessTitleSB = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4px;
`;

const BusinessFeaturesRowSB = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
`;

const BusinessFeatureItemSB = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);

  &:not(:last-child)::after {
    content: "·";
    margin: 0 10px;
    color: rgba(255, 255, 255, 0.25);
  }
`;

const BusinessCTABtnSB = styled.button`
  height: 48px;
  padding: 0 32px;
  background: #ffffff;
  border: none;
  border-radius: ${radius.pill}px;
  color: #090e19;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: #f0fdfb;
  }
`;

export default SubscriptionPlansPage;

// ===========================================================================
// LEGACY — personalized plan cards. DO NOT DELETE.
// Kept until the backend returns plan prices and subscription/checkout links
// via the API. When that lands, restore the implementation below (re-enable its
// imports, the sibling ./constants file, and the img-business asset) and remove
// the Stripe pricing table above.
// ===========================================================================
// import { Authenticated, useTranslate } from "@refinedev/core";
// import imgBusiness from "../../assets/img-business.png";
// import styled, { css } from "styled-components";
// import { CheckCircleIcon, LightningIcon } from "@phosphor-icons/react";
// import {
//   BUSINESS_FEATURES,
//   FREE_FEATURES,
//   PRO_FEATURES,
//   TEAM_FEATURES,
// } from "./constants";
// import { useEffect, useMemo, useRef, useState } from "react";
// import { useGetProducts } from "./hooks/useGetProducts";
// import { useGetActiveSubscriptions } from "pages/pricing/hooks/useGetActiveSubscriptions";
// import { ProductDefinition } from "@/types";
//
// type Billing = "monthly" | "annually";
// type Tier = "free" | "pro" | "team";
//
// //TODO - Map right product once API has correct names. Currently API products are named after
// // the old tiers, so "Team" product corresponds to "Pro" tier, and "Business" product corresponds to "Team" tier.
// //TODO - Map to right product price once API has correct product with prices.
//
// // API product/license names differ from the UI plan labels:
// // UI "Free" -> API "Free"
// // UI "Pro"  -> API "Team"
// // UI "Team" -> API "Business"
// const PRODUCT_NAME_BY_TIER = {
//   pro: "Team",
//   team: "Business",
// } as const;
//
// const LICENSE_NAME_BY_TIER: Record<Tier, string> = {
//   free: "Free",
//   pro: "Team",
//   team: "Business",
// };
//
// interface PlanPricing {
//   /** Price displayed on the card (per month, in major currency units) */
//   displayPrice: string;
//   /** Yearly total shown in the "billed annually" note (major currency units) */
//   yearlyTotal: string;
//   /** Currency symbol */
//   currency: string;
// }
//
// const parseAmount = (amount: number | string): number =>
//   typeof amount === "string" ? parseFloat(amount) : amount;
//
// const getCurrencySymbol = (currency: string): string => {
//   const symbols: Record<string, string> = { eur: "€", usd: "$", gbp: "£" };
//   return symbols[currency.toLowerCase()] ?? currency.toUpperCase();
// };
//
// const findPlanPricing = (
//   products: ProductDefinition[],
//   productName: string,
//   billing: Billing
// ): PlanPricing | null => {
//   const product = products.find(
//     (p) =>
//       p.product_type === "Plan" &&
//       p.name.toLowerCase() === productName.toLowerCase()
//   );
//   if (!product) return null;
//
//   // Products can have multiple prices per interval (old + new).
//   // Use the last one in the array as it represents the current price.
//   const monthPrices = product.prices.filter((pr) => pr.interval === "month");
//   const yearPrices = product.prices.filter((pr) => pr.interval === "year");
//   const monthPrice = monthPrices[monthPrices.length - 1];
//   const yearPrice = yearPrices[yearPrices.length - 1];
//
//   if (billing === "monthly") {
//     if (!monthPrice) return null;
//     const amount = parseAmount(monthPrice.amount);
//     return {
//       displayPrice: String(Math.round(amount)),
//       yearlyTotal: "",
//       currency: getCurrencySymbol(monthPrice.currency),
//     };
//   }
//
//   // Annual billing: show yearly price / 12 as monthly, and yearly total
//   if (!yearPrice) return null;
//   const yearlyAmount = parseAmount(yearPrice.amount);
//   const monthlyFromYearly = yearlyAmount / 12;
//   return {
//     displayPrice: String(Math.round(monthlyFromYearly)),
//     yearlyTotal: String(Math.round(yearlyAmount)),
//     currency: getCurrencySymbol(yearPrice.currency),
//   };
// };
//
// const SubscriptionPlansPage = () => {
//   const t = useTranslate();
//   const [billing, setBilling] = useState<Billing>("annually");
//   const carouselRef = useRef<HTMLDivElement>(null);
//   const proCardRef = useRef<HTMLDivElement>(null);
//
//   const { products } = useGetProducts();
//   const { subscriptions } = useGetActiveSubscriptions();
//   const currentLicenseName = subscriptions?.license_name;
//   const isCurrentTier = (tier: Tier) =>
//     currentLicenseName === LICENSE_NAME_BY_TIER[tier];
//
//   const proPricing = useMemo(
//     () => findPlanPricing(products, PRODUCT_NAME_BY_TIER.pro, billing),
//     [products, billing]
//   );
//   const teamPricing = useMemo(
//     () => findPlanPricing(products, PRODUCT_NAME_BY_TIER.team, billing),
//     [products, billing]
//   );
//
//   useEffect(() => {
//     const carousel = carouselRef.current;
//     const proCard = proCardRef.current;
//     if (!carousel || !proCard || window.innerWidth > 768) return;
//     carousel.scrollLeft =
//       proCard.offsetLeft - (carousel.offsetWidth - proCard.offsetWidth) / 2;
//   }, []);
//
//   return (
//     <Authenticated key="subscriptionPlans">
//       <PageSB>
//         <HeaderSB>
//           <PageTitleSB>{t("subscriptionPlans.title")}</PageTitleSB>
//           <PageSubtitleSB>{t("subscriptionPlans.subtitle")}</PageSubtitleSB>
//         </HeaderSB>
//
//         <ToggleAreaSB>
//           <ToggleWrapSB>
//             <TogglePillSB>
//               <ToggleOptionSB
//                 type="button"
//                 $active={billing === "monthly"}
//                 onClick={() => setBilling("monthly")}
//               >
//                 {t("subscriptionPlans.billing.monthly")}
//               </ToggleOptionSB>
//               <ToggleOptionSB
//                 type="button"
//                 $active={billing === "annually"}
//                 onClick={() => setBilling("annually")}
//               >
//                 {t("subscriptionPlans.billing.annually")}
//               </ToggleOptionSB>
//             </TogglePillSB>
//             <SaveBadgeSB>{t("subscriptionPlans.billing.save")}</SaveBadgeSB>
//           </ToggleWrapSB>
//         </ToggleAreaSB>
//
//         <CardsGridSB ref={carouselRef}>
//           <PlanCardSB>
//             <CardTierSB>{t("subscriptionPlans.free.tier")}</CardTierSB>
//             <CardSubtitleSB>
//               {t("subscriptionPlans.free.subtitle")}
//             </CardSubtitleSB>
//             <PriceAreaSB>
//               <PriceAmountSB>{t("subscriptionPlans.free.price")}</PriceAmountSB>
//               <PriceUnitSB>{t("subscriptionPlans.free.priceUnit")}</PriceUnitSB>
//             </PriceAreaSB>
//             <DividerSB />
//             <CardBodySB>
//               <FeatureListSB>
//                 {FREE_FEATURES.map((f) => (
//                   <FeatureItemSB key={f}>
//                     <FeatureIconSB>
//                       <CheckCircleIcon
//                         size={16}
//                         color="#14b8a6"
//                         weight="fill"
//                       />
//                     </FeatureIconSB>
//                     {t(f)}
//                   </FeatureItemSB>
//                 ))}
//               </FeatureListSB>
//             </CardBodySB>
//             <CTACurrentPlanBtnSB type="button" disabled>
//               {isCurrentTier("free")
//                 ? t("subscriptionPlans.currentPlan")
//                 : t("subscriptionPlans.free.tier")}
//             </CTACurrentPlanBtnSB>
//           </PlanCardSB>
//
//           <PlanCardSB $pro ref={proCardRef}>
//             <RecommendedBadgeSB>
//               {t("subscriptionPlans.recommended")}
//             </RecommendedBadgeSB>
//             <CardTierSB>{t("subscriptionPlans.pro.tier")}</CardTierSB>
//             <CardSubtitleSB>
//               {t("subscriptionPlans.pro.subtitle")}
//             </CardSubtitleSB>
//             <PriceAreaSB>
//               <PriceAmountSB>
//                 {proPricing?.currency}
//                 {proPricing?.displayPrice}
//               </PriceAmountSB>
//               <PriceUnitSB>{t("subscriptionPlans.pro.priceUnit")}</PriceUnitSB>
//             </PriceAreaSB>
//             {billing === "annually" && proPricing && (
//               <PriceBilledSB>
//                 {t("subscriptionPlans.pro.billedAnnually", {
//                   amount: `${proPricing.currency}${proPricing.yearlyTotal}`,
//                 })}
//               </PriceBilledSB>
//             )}
//             <DividerSB />
//             <CardBodySB>
//               <FeatureListSB>
//                 {PRO_FEATURES.map((f) => (
//                   <FeatureItemSB key={f}>
//                     <FeatureIconSB>
//                       <CheckCircleIcon
//                         size={16}
//                         color="#14b8a6"
//                         weight="fill"
//                       />
//                     </FeatureIconSB>
//                     {t(f)}
//                   </FeatureItemSB>
//                 ))}
//               </FeatureListSB>
//             </CardBodySB>
//             {isCurrentTier("pro") ? (
//               <CTACurrentPlanBtnSB type="button" disabled>
//                 {t("subscriptionPlans.currentPlan")}
//               </CTACurrentPlanBtnSB>
//             ) : (
//               <>
//                 <CTAPrimaryBtnSB type="button">
//                   <LightningIcon size={14} weight="fill" color="white" />
//                   {t("subscriptionPlans.pro.cta")}
//                 </CTAPrimaryBtnSB>
//                 <CTANoteSB>{t("subscriptionPlans.noCreditCard")}</CTANoteSB>
//               </>
//             )}
//           </PlanCardSB>
//
//           <PlanCardSB>
//             <CardTierSB>{t("subscriptionPlans.team.tier")}</CardTierSB>
//             <CardSubtitleSB>
//               {t("subscriptionPlans.team.subtitle")}
//             </CardSubtitleSB>
//             <PriceAreaSB>
//               <PriceAmountSB>
//                 {teamPricing?.currency}
//                 {teamPricing?.displayPrice}
//               </PriceAmountSB>
//               <PriceUnitSB>{t("subscriptionPlans.team.priceUnit")}</PriceUnitSB>
//             </PriceAreaSB>
//             {billing === "annually" && teamPricing && (
//               <PriceBilledSB>
//                 {t("subscriptionPlans.team.billedAnnually", {
//                   amount: `${teamPricing.currency}${teamPricing.yearlyTotal}`,
//                 })}
//               </PriceBilledSB>
//             )}
//             <PriceNoteSB>
//               {t("subscriptionPlans.team.minimumSeats")}
//             </PriceNoteSB>
//             <DividerSB />
//             <CardBodySB>
//               <FeatureGroupLabelSB>
//                 {t("subscriptionPlans.team.everythingInPro")}
//               </FeatureGroupLabelSB>
//               <FeatureListSB>
//                 {TEAM_FEATURES.map((f) => (
//                   <FeatureItemSB key={f}>
//                     <FeatureIconSB>
//                       <CheckCircleIcon
//                         size={16}
//                         color="#14b8a6"
//                         weight="fill"
//                       />
//                     </FeatureIconSB>
//                     {t(f)}
//                   </FeatureItemSB>
//                 ))}
//               </FeatureListSB>
//             </CardBodySB>
//             {isCurrentTier("team") ? (
//               <CTACurrentPlanBtnSB type="button" disabled>
//                 {t("subscriptionPlans.currentPlan")}
//               </CTACurrentPlanBtnSB>
//             ) : (
//               <>
//                 <CTASecondaryBtnSB type="button">
//                   {t("subscriptionPlans.team.cta")}
//                 </CTASecondaryBtnSB>
//                 <CTANoteSB>{t("subscriptionPlans.noCreditCard")}</CTANoteSB>
//               </>
//             )}
//           </PlanCardSB>
//         </CardsGridSB>
//
//         <BusinessBannerSB>
//           <BusinessImgSB src={imgBusiness} alt="" />
//           <BusinessLeftSB>
//             <BusinessLabelSB>
//               {t("subscriptionPlans.business.label")}
//             </BusinessLabelSB>
//             <BusinessTitleSB>
//               {t("subscriptionPlans.business.title")}
//             </BusinessTitleSB>
//             <BusinessDescription>
//               {t("subscriptionPlans.business.description")}
//             </BusinessDescription>
//             <BusinessFeaturesRowSB>
//               {BUSINESS_FEATURES.map((f) => (
//                 <BusinessFeatureItemSB key={f}>{t(f)}</BusinessFeatureItemSB>
//               ))}
//             </BusinessFeaturesRowSB>
//           </BusinessLeftSB>
//           <BusinessCTABtnSB type="button">
//             {t("subscriptionPlans.business.cta")}
//           </BusinessCTABtnSB>
//         </BusinessBannerSB>
//       </PageSB>
//     </Authenticated>
//   );
// };
//
// const PageSB = styled.div`
//   max-width: 1100px;
//   margin: 0 auto;
//   padding: 64px 24px;
//
//   @media (width <= 768px) {
//     padding: 64px 16px 48px;
//   }
// `;
//
// const HeaderSB = styled.div`
//   text-align: center;
//   margin-bottom: 32px;
// `;
//
// const PageTitleSB = styled.h1`
//   font-family: "Poppins", sans-serif;
//   font-size: 48px;
//   font-weight: 600;
//   color: #090e19;
//   margin: 0 0 10px;
//   letter-spacing: -0.04em;
// `;
//
// const PageSubtitleSB = styled.p`
//   font-size: 15px;
//   color: #6b7280;
//   margin: 0;
// `;
//
// const ToggleAreaSB = styled.div`
//   display: flex;
//   justify-content: center;
//   margin-bottom: 48px;
// `;
//
// const ToggleWrapSB = styled.div`
//   position: relative;
//   display: inline-flex;
// `;
//
// const TogglePillSB = styled.div`
//   display: inline-flex;
//   background: #f3f4f6;
//   border-radius: 999px;
//   padding: 4px;
// `;
//
// const ToggleOptionSB = styled.button<{ $active: boolean }>`
//   height: 38px;
//   padding: 0 22px;
//   border: none;
//   border-radius: 999px;
//   font-size: 14px;
//   font-weight: ${(p) => (p.$active ? 600 : 400)};
//   color: ${(p) => (p.$active ? "#090e19" : "#9ca3af")};
//   background: ${(p) => (p.$active ? "#ffffff" : "transparent")};
//   box-shadow: ${(p) => (p.$active ? "0 1px 5px rgba(0, 0, 0, 0.12)" : "none")};
//   cursor: pointer;
//   transition:
//     background 0.2s,
//     color 0.2s,
//     box-shadow 0.2s;
//   white-space: nowrap;
// `;
//
// const SaveBadgeSB = styled.span`
//   position: absolute;
//   top: -12px;
//   right: -14px;
//   font-size: 10px;
//   font-weight: 700;
//   color: #ffffff;
//   background: #14b8a6;
//   border-radius: 999px;
//   padding: 3px 8px;
//   transform: rotate(12deg);
//   transform-origin: center;
//   white-space: nowrap;
//   pointer-events: none;
// `;
//
// const CardsGridSB = styled.div`
//   display: grid;
//   grid-template-columns: 1fr 1fr 1fr;
//   gap: 20px;
//   align-items: stretch;
//   padding-top: 22px;
//   margin-bottom: 24px;
//
//   @media (width <= 768px) {
//     display: flex;
//     overflow-x: scroll;
//     -webkit-overflow-scrolling: touch;
//     scroll-snap-type: x mandatory;
//     overscroll-behavior-x: contain;
//     gap: 12px;
//     padding: 22px 10vw 24px;
//     margin: 0 -16px 24px;
//     align-items: stretch;
//     scrollbar-width: none;
//     &::-webkit-scrollbar {
//       display: none;
//     }
//   }
// `;
//
// const PlanCardSB = styled.div<{ $pro?: boolean }>`
//   border-radius: 16px;
//   padding: 28px 24px;
//   display: flex;
//   flex-direction: column;
//   position: relative;
//   ${(p) =>
//     p.$pro
//       ? css`
//           border: 2px solid transparent;
//           background:
//             linear-gradient(#ffffff, #ffffff) padding-box,
//             linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%) border-box;
//           box-shadow: 0 8px 32px -8px rgba(109, 40, 217, 0.2);
//         `
//       : css`
//           background: #ffffff;
//           border: 1px solid #e5e7eb;
//         `}
//
//   @media (width <= 768px) {
//     flex: 0 0 80vw;
//     scroll-snap-align: center;
//   }
// `;
//
// const CardTierSB = styled.h2`
//   font-family: "Poppins", sans-serif;
//   font-size: 22px;
//   font-weight: 600;
//   color: #090e19;
//   margin: 0 0 6px;
//   letter-spacing: -0.02em;
// `;
//
// const CardSubtitleSB = styled.p`
//   font-size: 13px;
//   color: #6b7280;
//   margin: 0;
//   line-height: 1.5;
// `;
//
// const PriceAreaSB = styled.div`
//   margin: 20px 0 2px;
// `;
//
// const PriceAmountSB = styled.span`
//   font-family: "Poppins", sans-serif;
//   font-size: 40px;
//   font-weight: 700;
//   color: #090e19;
//   letter-spacing: -0.03em;
// `;
//
// const PriceUnitSB = styled.span`
//   font-size: 14px;
//   color: #6b7280;
//   margin-left: 4px;
// `;
//
// const DividerSB = styled.hr`
//   border: none;
//   border-top: 1px solid #f3f4f6;
//   margin: 20px 0;
// `;
//
// const CardBodySB = styled.div`
//   flex: 1;
// `;
//
// const FeatureListSB = styled.ul`
//   list-style: none;
//   padding: 0;
//   margin: 0 0 24px;
//   display: flex;
//   flex-direction: column;
//   gap: 10px;
// `;
//
// const FeatureItemSB = styled.li`
//   display: flex;
//   align-items: flex-start;
//   gap: 8px;
//   font-size: 13px;
//   color: #374151;
//   line-height: 1.45;
// `;
//
// const FeatureIconSB = styled.span`
//   flex-shrink: 0;
//   margin-top: 1px;
// `;
//
// const CTACurrentPlanBtnSB = styled.button`
//   width: 100%;
//   height: 44px;
//   background: #f3f4f6;
//   border: 1px solid #e5e7eb;
//   border-radius: 999px;
//   color: #9ca3af;
//   font-size: 14px;
//   font-weight: 600;
//   cursor: default;
// `;
//
// const RecommendedBadgeSB = styled.div`
//   position: absolute;
//   top: -14px;
//   left: 50%;
//   transform: translateX(-50%);
//   background: linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%);
//   color: #ffffff;
//   font-size: 11px;
//   font-weight: 700;
//   letter-spacing: 0.06em;
//   text-transform: uppercase;
//   padding: 8px 16px;
//   border-radius: 999px;
//   white-space: nowrap;
// `;
//
// const PriceBilledSB = styled.p`
//   font-size: 12px;
//   color: #9ca3af;
//   margin: 2px 0 0;
// `;
//
// const CTAPrimaryBtnSB = styled.button`
//   width: 100%;
//   height: 44px;
//   background: linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%);
//   border: 3px solid #3bd4ca;
//   border-radius: 999px;
//   color: #ffffff;
//   font-size: 14px;
//   font-weight: 600;
//   cursor: pointer;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   gap: 7px;
//   transition:
//     opacity 0.2s,
//     transform 0.2s;
//
//   &:hover {
//     opacity: 0.92;
//     transform: translateY(-1px);
//   }
// `;
//
// const CTANoteSB = styled.p`
//   font-size: 12px;
//   color: #9ca3af;
//   text-align: center;
//   margin: 8px 0 0;
// `;
//
// const PriceNoteSB = styled.p`
//   font-size: 12px;
//   color: #9ca3af;
//   margin: 4px 0 0;
// `;
//
// const FeatureGroupLabelSB = styled.p`
//   font-size: 11px;
//   font-weight: 600;
//   color: #9ca3af;
//   text-transform: uppercase;
//   letter-spacing: 0.06em;
//   margin: 0 0 10px;
// `;
//
// const CTASecondaryBtnSB = styled.button`
//   width: 100%;
//   height: 44px;
//   background: #ffffff;
//   border: 1.5px solid #10b981;
//   border-radius: 999px;
//   color: #10b981;
//   font-size: 14px;
//   font-weight: 600;
//   cursor: pointer;
//   transition: background 0.15s;
//
//   &:hover {
//     background: #ecfdf5;
//   }
// `;
//
// const BusinessBannerSB = styled.div`
//   background: #111827;
//   border-radius: 16px;
//   padding: 40px 48px;
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   gap: 48px;
//   overflow: hidden;
//
//   @media (width <= 768px) {
//     flex-direction: column;
//     align-items: flex-start;
//     padding: 32px 24px;
//     gap: 28px;
//   }
// `;
//
// const BusinessImgSB = styled.img`
//   width: 20%;
//   flex-shrink: 0;
//   align-self: stretch;
//   object-fit: cover;
//   border-radius: 10px;
//   margin: -40px 0 -40px -48px;
//
//   @media (width <= 768px) {
//     display: none;
//   }
// `;
//
// const BusinessLeftSB = styled.div`
//   flex: 1;
//   min-width: 0;
// `;
//
// const BusinessLabelSB = styled.span`
//   font-size: 11px;
//   font-weight: 700;
//   color: #14b8a6;
//   text-transform: uppercase;
//   letter-spacing: 0.08em;
//   margin-bottom: 10px;
//   display: block;
// `;
//
// const BusinessDescription = styled.h3`
//   font-size: 15px;
//   font-weight: 300;
//   color: #ffffff;
//   margin: 0 0 24px;
//   line-height: 1.4;
//   max-width: 520px;
// `;
//
// const BusinessTitleSB = styled.div`
//   font-family: "Poppins", sans-serif;
//   font-size: 48px;
//   font-weight: 700;
//   color: #ffffff;
//   margin-bottom: 4px;
// `;
//
// const BusinessFeaturesRowSB = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   row-gap: 4px;
// `;
//
// const BusinessFeatureItemSB = styled.span`
//   font-size: 13px;
//   color: rgba(255, 255, 255, 0.55);
//
//   &:not(:last-child)::after {
//     content: "·";
//     margin: 0 10px;
//     color: rgba(255, 255, 255, 0.25);
//   }
// `;
//
// const BusinessCTABtnSB = styled.button`
//   height: 48px;
//   padding: 0 32px;
//   background: #ffffff;
//   border: none;
//   border-radius: 999px;
//   color: #090e19;
//   font-size: 15px;
//   font-weight: 600;
//   cursor: pointer;
//   white-space: nowrap;
//   flex-shrink: 0;
//   transition: background 0.15s;
//
//   &:hover {
//     background: #f0fdfb;
//   }
// `;
//
// export default SubscriptionPlansPage;
